/**
 * vue-i18n setup for the FusionWeb UI.
 *
 * The active locale is driven by the user's "System Language" setting (Ux.Language), applied
 * through the main store's setLanguage()/initLanguage()/applyProfileLanguage() actions — never
 * set i18n.global.locale directly from a component.
 *
 * Locale codes: Ux.Language stores full tags (en-US, es-MX, fr-FR). The base catalogs are keyed by
 * primary subtag (en, es, fr); normalizeLocale() collapses unknown regional subtags the same way
 * the store's getStatusMessage() getter does (split('-')[0].toLowerCase(), fall back to English),
 * so the bundled catalogs and the server-injected status-message table always agree on a language.
 * A small set of REGIONAL_LOCALES ('en-GB', 'fr-CA', 'fr-FR') are registered as override catalogs
 * that carry ONLY the keys differing from their base catalog — everything else falls through via
 * fallbackLocale. normalizeLocale() returns the regional key when the tag matches one, so
 * 'fr-FR' resolves to the France-French overrides while 'fr-CA' and bare 'fr' stay on the
 * Canadian French base.
 *
 * Regional convention for the non-English catalogs follows the resolved product decision in
 * docs/plans/2026-06-09-localization-translation-review.md: es => Mexican Spanish (informal "tú"),
 * fr => Canadian French (formal "vous"). "Dealership" is "concesionario" / "concession" to match
 * the reviewed backend copy in StatusMessages.cs and DiplomaticResponses.cs.
 *
 * Instance gating: which of the registered catalogs are actually offered is driven by the server.
 * AppController serialises the instance's supported reply languages (env var
 * FUSION_SUPPORTED_REPLY_LANGUAGES — the same set that gates Rey's conversation languages) into
 * window.__fusionSupportedReplyLanguages in Index/Toolbar.cshtml. German stays registered in the
 * catalog but is only selectable on instances that opted in. When the global is absent (specs,
 * dev builds, entry points that never set it) every registered catalog stays enabled.
 */
import { createI18n } from 'vue-i18n';

import en from './locales/en.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import de from './locales/de.json';
import enGB from './locales/en-GB.json';
import frCA from './locales/fr-CA.json';
import frFR from './locales/fr-FR.json';

export const SUPPORTED_LOCALES = ['en', 'es', 'fr', 'de'];
export const DEFAULT_LOCALE = 'en';

/**
 * Regional override catalogs registered alongside the base catalogs. Each holds ONLY the keys
 * whose value differs from its base primary-subtag catalog (en-GB -> en, fr-CA/fr-FR -> fr);
 * unlisted keys resolve through fallbackLocale. fr-CA is registered but currently empty because
 * the fr base catalog is already Canadian French. Server gating stays on the primary subtag — a
 * regional catalog is only reachable when its primary language is enabled.
 */
export const REGIONAL_LOCALES = ['en-GB', 'fr-CA', 'fr-FR'];

/**
 * Server-injected instance allow-list (primary tags) from
 * window.__fusionSupportedReplyLanguages — null when the Razor view never emitted it, in which
 * case the full registered set applies (specs + dev builds).
 */
function serverSupportedLocales() {
  const raw = typeof window !== 'undefined' ? window.__fusionSupportedReplyLanguages : null;
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const tags = raw
    .filter(tag => typeof tag === 'string' && tag)
    .map(tag => tag.split('-')[0].toLowerCase());
  return tags.length > 0 ? tags : null;
}

/**
 * Locales the user may actually select: the registered catalogs intersected with the server's
 * supported reply languages. Registered-first order is preserved so the settings dropdown keeps
 * its stable ordering regardless of the server list's order. Falls back to the full registered
 * list when the server global is absent (specs, dev builds) so nothing is hidden there.
 */
export function getEnabledLocales() {
  const server = serverSupportedLocales();
  if (!server) return SUPPORTED_LOCALES;
  return SUPPORTED_LOCALES.filter(locale => server.includes(locale));
}

/**
 * Collapse a language tag (or anything else) to a supported catalog locale. Unsupported/absent
 * values fall back to English rather than throwing, so a stale or unexpected Ux.Language value
 * degrades to English copy instead of blanking the UI. "Supported" here means enabled on this
 * instance (getEnabledLocales), so e.g. German collapses to English on an instance that has not
 * opted in. A tag matching a regional catalog (case-insensitive) resolves to that regional key —
 * 'fr-ca' -> 'fr-CA' — provided the primary language is enabled.
 */
export function normalizeLocale(language) {
  if (typeof language !== 'string' || !language) return DEFAULT_LOCALE;

  const subtag = language.split('-')[0].toLowerCase();
  if (!getEnabledLocales().includes(subtag)) return DEFAULT_LOCALE;

  const regional = REGIONAL_LOCALES.find(locale => locale.toLowerCase() === language.toLowerCase());
  return regional || subtag;
}

/**
 * Lower-case primary subtag of a language tag ('fr-FR' -> 'fr', 'EN' -> 'en'). Unlike
 * normalizeLocale this performs no enabled-locale gating — callers use it to compare languages,
 * not to pick a catalog. Defaults to 'en' for absent/garbage values.
 */
export function primaryLanguage(language) {
  if (typeof language !== 'string' || !language) return DEFAULT_LOCALE;
  const subtag = language.split('-')[0].toLowerCase();
  return subtag || DEFAULT_LOCALE;
}

/**
 * The full BCP-47 tag last applied through setLocale (the user's stored tag when known, e.g.
 * 'fr-FR'), else the canonical regional mapping of the active primary subtag (en -> en-US,
 * es -> es-MX, fr -> fr-CA, de -> de-DE). Components use this for Intl formatting and the
 * document lang attribute; the catalog choice itself still goes through normalizeLocale().
 * Pure function of module state — safe to call from any component at any time.
 */
let lastAppliedLanguageTag = null;

const DEFAULT_REGION_BY_PRIMARY = { en: 'en-US', es: 'es-MX', fr: 'fr-CA', de: 'de-DE' };

export function getActiveLocaleTag() {
  if (lastAppliedLanguageTag) return lastAppliedLanguageTag;
  // When no full tag was recorded (locale set to a bare subtag), map the active catalog's primary
  // language onto the product's canonical regional tag.
  const primary = primaryLanguage(i18n.global.locale.value);
  return DEFAULT_REGION_BY_PRIMARY[primary] || 'en-US';
}

const i18n = createI18n({
  // Composition mode (legacy: false) with globalInjection so $t() works in Options API templates
  // and this.$t() works in methods. The Legacy API is deprecated in vue-i18n 11 and removed in 12.
  legacy: false,
  globalInjection: true,
  locale: DEFAULT_LOCALE,
  // Object-form fallback: each regional override catalog resolves its missing keys through its
  // base primary-subtag catalog first (and English last). The base catalogs themselves keep the
  // plain 'en' default.
  fallbackLocale: {
    'en-GB': ['en'],
    'fr-CA': ['fr', 'en'],
    'fr-FR': ['fr', 'en'],
    default: ['en'],
  },
  // Keep `de` registered even when the instance gates it out — the catalog ships in the bundle so
  // an instance can flip German on without a rebuild; getEnabledLocales()/normalizeLocale() keep it
  // unselectable in the meantime. Regional override catalogs are registered for the same reason.
  messages: { en, es, fr, de, 'en-GB': enGB, 'fr-CA': frCA, 'fr-FR': frFR },
  // Keep the console quiet outside local dev; the catalog key-parity spec is what actually guards
  // against missing keys.
  missingWarn: import.meta.env.MODE === 'development',
  fallbackWarn: import.meta.env.MODE === 'development',
});

/**
 * Point i18n at the catalog for the given language tag. Returns the normalized locale actually
 * applied so callers can log/store what took effect. Reactive — every $t() binding re-renders.
 * Only enabled locales can be applied (see normalizeLocale); anything else lands on English.
 * Also records the full tag for getActiveLocaleTag(), and mirrors it onto <html lang> so screen
 * readers and browser translation pick up the actual language.
 *
 * The tag is recorded ONLY when normalizeLocale resolved it to a registered regional override
 * ('en-GB', 'fr-CA', 'fr-FR') — the one case where the user's exact regional choice differs from
 * the default mapping. Anything else (a bare subtag, an unregistered region like 'es-ES' where the
 * es catalog is Mexican Spanish by product decision, a gated-out 'de-DE' on an instance without
 * de, an unsupported 'pt-BR', a malformed 'fr-') leaves lastAppliedLanguageTag null and falls
 * back to the default regional mapping of the applied primary. That keeps English UIs from
 * carrying foreign Intl dates and <html lang> values, and — because only the canonical
 * REGIONAL_LOCALES keys are ever recorded — no malformed tag can reach the Intl call sites in
 * components (MessageThread.vue, MessengerWindow.vue) as a RangeError.
 */
export function setLocale(language) {
  const locale = normalizeLocale(language);
  i18n.global.locale.value = locale;
  lastAppliedLanguageTag = REGIONAL_LOCALES.includes(locale) ? locale : null;
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.lang = getActiveLocaleTag();
  }
  return locale;
}

export function currentLocale() {
  return i18n.global.locale.value;
}

export default i18n;
